import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { FolderKanban, FileAudio, FileText, Image, GripVertical, Paperclip } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  high: "priority-high",
  medium: "priority-medium",
  low: "priority-low"
};

const PRIORITY_LABELS = {
  high: "Alta",
  medium: "Média",
  low: "Baixa"
};

export default function PublicProjectPage() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/public/project/${shareToken}`);
        setData(response.data);
      } catch (err) {
        console.error("Fetch public project error:", err);
        setError(err.response?.status === 404 ? "Projeto não encontrado" : "Erro ao carregar projeto");
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [shareToken]);

  const getFileIcon = (type) => {
    switch (type) {
      case "audio": return <FileAudio className="h-4 w-4" />;
      case "image": return <Image className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <span className="text-muted-foreground">Carregando projeto...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <FolderKanban className="h-16 w-16 mx-auto text-muted-foreground/50" />
          <h1 className="text-2xl font-bold mt-4 heading-font">{error}</h1>
          <p className="text-muted-foreground mt-2">O link pode estar incorreto ou o projeto foi removido</p>
        </div>
      </div>
    );
  }

  const { project, client, columns, tasks, files } = data;

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="public-project-page">
      {/* Header */}
      <header className="border-b px-6 py-4">
        <div className="flex items-center gap-2">
          <FolderKanban className="h-6 w-6 text-accent" />
          <span className="font-semibold heading-font">Crowd Board</span>
          <span className="text-muted-foreground">• Visualização Pública</span>
        </div>
      </header>

      {/* Cover Image */}
      <div
        className="h-48 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${project?.cover_image})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60" />
        <div className="absolute bottom-4 left-6 right-6">
          <p className="text-white/80 text-sm mb-1">Projeto de {client?.name}</p>
          <h1 className="text-3xl font-bold text-white heading-font">{project?.name}</h1>
          {project?.description && (
            <p className="text-white/80 mt-1 max-w-2xl">{project.description}</p>
          )}
        </div>
      </div>

      {/* Kanban Board */}
      <ScrollArea className="flex-1">
        <div className="flex gap-6 p-6 min-h-full items-start">
          {columns.map((column) => (
            <div
              key={column.id}
              className="w-80 shrink-0 flex flex-col bg-muted/50 rounded-lg"
              data-testid={`public-column-${column.id}`}
            >
              <div className="p-3 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{column.name}</h3>
                  <Badge variant="secondary" className="text-xs">
                    {tasks.filter(t => t.column_id === column.id).length}
                  </Badge>
                </div>
              </div>
              <div className="p-3 space-y-3 min-h-[200px]">
                {tasks
                  .filter(t => t.column_id === column.id)
                  .map((task) => (
                    <div
                      key={task.id}
                      className="bg-background p-3 rounded-lg shadow-sm border border-border"
                      data-testid={`public-task-${task.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0 opacity-30" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm">{task.title}</h4>
                          {task.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{task.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge className={`text-xs ${PRIORITY_COLORS[task.priority]}`}>
                              {PRIORITY_LABELS[task.priority]}
                            </Badge>
                            {task.attachments?.length > 0 && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Paperclip className="h-3 w-3" />
                                {task.attachments.length}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      {/* Files Section */}
      {files.length > 0 && (
        <div className="border-t p-6">
          <h2 className="font-semibold mb-4 heading-font">Arquivos do Projeto</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {files.map(file => (
              <div key={file.id} className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg" data-testid={`public-file-${file.id}`}>
                {getFileIcon(file.file_type)}
                <span className="text-sm truncate">{file.original_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t px-6 py-4 text-center text-sm text-muted-foreground">
        Visualização compartilhada via <span className="font-medium text-accent">Crowd Board</span>
      </footer>
    </div>
  );
}
